import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Magic-Link-Landing — wird vom Email-Link aufgerufen
 * (`/auth/magic?token=…`). Wir tauschen den Magic-Token gegen
 * einen Access-Token und navigieren weiter. Auf Mobile öffnet der
 * Universal Link direkt die App; falls die App nicht installiert
 * ist, landet der User hier im Browser und kann sich trotzdem
 * einloggen.
 */
const MagicLinkPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('Kein Token in der URL');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/auth/otp/magic?token=${encodeURIComponent(token)}`
        );
        if (loginWithToken) await loginWithToken(data.access_token, data.refresh_token);
        else localStorage.setItem('token', data.access_token);
        toast.success('Erfolgreich eingeloggt');
        navigate('/');
      } catch (err) {
        setStatus('error');
        setErrorMessage(
          err.response?.data?.detail || 'Link ungültig oder abgelaufen'
        );
      }
    })();
  }, [params, navigate, loginWithToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-10 max-w-md text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-yellow-400 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Login wird verifiziert…</h1>
            <p className="text-white/60 text-sm">Du wirst gleich weitergeleitet.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Login fehlgeschlagen</h1>
            <p className="text-white/60 text-sm mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-lg"
              data-testid="magic-link-back-to-login"
            >
              Zurück zum Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkPage;
